// 🔧 core_pipeline_factory.js
// ✅ Purpose: Builds the execution pipeline for the LGV Core Package Demo Engine
// ✅ Features:
// - Returns pipeline steps in execution order
// - Allows future add-ons to inject steps
// - Keeps Core Builder focused on orchestration only
// - Fully block-commented for clarity and future devs

import { createEventStep } from "./steps/event_step";
import { createCategoryStep } from "./steps/category_step";
import { createScoringStep } from "./steps/scoring_step";
import { createEntryStep } from "./steps/entry_step";
import { createUserStep } from "./steps/user_step";
import { createVoteStep } from "./steps/vote_step";
import { createHomepageStep } from "./steps/homepage_step";
import { createScheduleStep } from "./steps/schedule_step";
import { createMapStep } from "./steps/map_step";
import { createContentStep } from "./steps/content_step";
import { createSeoSeedStep } from "./steps/seo_seed_step";
import { createSponsorStep } from "./steps/sponsor_step";
import { createVolunteerStep } from "./steps/volunteer_step";
import { createVendorStep } from "./steps/vendor_step";
import { createAuctionStep } from "./steps/auction_step";
import { createStoreStep } from "./steps/store_step";
import { createTicketStep } from "./steps/ticket_step";
import { createPassportStep } from "./steps/passport_step";
import { createSocialStep } from "./steps/social_step";
import { DEMO_ADDON_OPTIONS } from "../config/demo_options";

export const createCorePipeline = ({
  demoConfig,
  seedProfile,
} = {}) => {
    const pipeline = [
    createEventStep(),
    createContentStep(),
    createCategoryStep(),

    // ===================================================
    // Experience scoring configuration
    // ===================================================
    // Runs after the event/category foundation exists and
    // before entries, demo users, or votes are generated.
    // Industries without scoring metadata safely no-op.
    // ===================================================

    createScoringStep(),

    createEntryStep(),
    createUserStep(),
    createVoteStep(),
    createScheduleStep(),   
    createMapStep(),
    createHomepageStep(),
    createVolunteerStep(),
    ];

  // =====================================================
  // Add-on Engines
  // =====================================================

  const addOnStepFactories = {
    sponsors: createSponsorStep,
    vendors: createVendorStep,
    tickets: createTicketStep,
    auction: createAuctionStep,
    passport: createPassportStep,
    store: createStoreStep,
    social: createSocialStep,
  };

  DEMO_ADDON_OPTIONS.forEach(({ key }) => {
    if (demoConfig?.addOns?.[key] && addOnStepFactories[key]) {
      pipeline.push(addOnStepFactories[key]());
    }
  });

  // SEO research must run after all selected data sources exist so generated
  // drafts can use categories, volunteers, sponsors, and auction items.
  pipeline.push(createSeoSeedStep());

  return pipeline;
};
